import React from 'react';
import { Grid, Button, Menu, MenuItem } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import AddIcon from '@material-ui/icons/Add';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import DocumentRepository from '../DocumentRepository';
import { LABEL_TO_KEY } from '../DocumentRepository/repositoryData';
import { useFeaturesAccess } from '../utils/useFeaturesAccess';
import { useProjectDevNav } from '../ProjectDevNavContext';

// Session Storage Keys for Project Development
const STORAGE_KEYS = {
  FORM_DATA: "productDevelopmentFormData",
  CURRENT_STEP: "productDevelopmentCurrentStep",
  COMPLETED_STEPS: "productDevelopmentCompletedSteps",
};

// UI-PD-Changes: Document Repository dropdown options (replaces the
// View Active Batches button per the updated design).
const DOCUMENT_REPOSITORY_OPTIONS = [
  'Tech Transfer',
  'Manufacturing',
  'Quality',
  'Analytical',
  'Safety',
];

const ActionButtons = ({ classes, setOpenActiveBatchesModal }) => {
  const history = useHistory();
  const { openProjectAnalytics, openExecutiveSummary } = useProjectDevNav();

  // Remember the Document Repository's last-used filter selection so closing and
  // reopening the popup restores it ("most recently used"). In-memory for the
  // life of the dashboard — first-ever open falls back to the user's saved
  // filter (see DocumentRepository). No re-render needed, so a ref is enough.
  const recentRepoFiltersRef = React.useRef(null);
  const handleRepoFiltersChange = React.useCallback((filters) => {
    recentRepoFiltersRef.current = filters;
  }, []);

  // Screen 26 feature access (fetched in ProjectDevelopment/index.jsx).
  // Admins see all buttons regardless of the stored flags.
  const featuresAccess = useFeaturesAccess();

  // UI-PD-Changes: anchor state for the Document Repository dropdown
  const [docRepoAnchorEl, setDocRepoAnchorEl] = React.useState(null);

  const handleDocRepoClick = (event) => {
    setDocRepoAnchorEl(event.currentTarget);
  };

  const handleDocRepoClose = () => {
    setDocRepoAnchorEl(null);
  };

  // UI-PD-Changes: which repository modal is open (null = closed).
  const [openRepoKey, setOpenRepoKey] = React.useState(null);

  const handleDocRepoSelect = (option) => {
    // Open the static Document Repository modal for the chosen repository.
    setOpenRepoKey(LABEL_TO_KEY[option] || 'techTransfer');
    setDocRepoAnchorEl(null);
  };

  const handleAddNewProject = () => {
    // Clear session storage to start fresh (same as "Add New Product" does)
    Object.values(STORAGE_KEYS).forEach(key => {
      sessionStorage.removeItem(key);
    });

    // Navigate to Add New Project page
    history.push(
      `/pcmp/${localStorage.collabType}/${localStorage.roleName}/${localStorage.organizationName}/${localStorage.ecosystemId}/productdevelopment/add-new-project`
    );
  };

  return (
    <Grid
      item
      xs={12}
      sm="auto"
      style={{
        display: 'flex',
        gap: '1rem',
        alignItems: 'flex-end',
        flexWrap: 'wrap',
      }}
    >
      {/* UI-PD-Changes: View Active Batches button replaced by the
          Document Repository dropdown below. Functionality kept commented
          (modal + state still exist in ModalsSection / hooks):
      <Button
        variant="outlined"
        className={classes.outlineButton}
        onClick={() => setOpenActiveBatchesModal(true)}
      >
        View Active Batches
      </Button>
      */}
      {featuresAccess?.documentRepositoryView && (
        <>
          <Button
            variant="outlined"
            className={classes.outlineButton}
            onClick={handleDocRepoClick}
            endIcon={<ArrowDropDownIcon />}
          >
            Document Repository
          </Button>
          <Menu
            anchorEl={docRepoAnchorEl}
            open={Boolean(docRepoAnchorEl)}
            onClose={handleDocRepoClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            getContentAnchorEl={null}
          >
            {/* Each option opens the Document Repository popup scoped to that
                category (Tech Transfer / Manufacturing / Quality / Analytical /
                Safety). */}
            {DOCUMENT_REPOSITORY_OPTIONS.map((option) => (
              <MenuItem
                key={option}
                onClick={() => handleDocRepoSelect(option)}
              >
                {option}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
      {/* Executive Summary is shown but disabled for now. */}
      {featuresAccess?.executiveSummary && (
        <Button
          variant="outlined"
          className={classes.outlineButton}
          onClick={openExecutiveSummary}
          disabled
          style={{ borderRadius: '20px' }}
        >
          Executive Summary
        </Button>
      )}
      {featuresAccess?.projectAnalytics && (
        <Button
          variant="contained"
          className={classes.blueButton}
          onClick={openProjectAnalytics}
        >
          Batch Analytics
        </Button>
      )}

      {/* UI-PD-Changes: static Document Repository modal, opened from the
          Document Repository dropdown above. */}
      <DocumentRepository
        open={!!openRepoKey}
        repositoryKey={openRepoKey}
        onClose={() => setOpenRepoKey(null)}
        recentFilters={recentRepoFiltersRef.current}
        onFiltersChange={handleRepoFiltersChange}
      />
    </Grid>
  );
};

export default ActionButtons;
